import React, { useState } from 'react'

import { theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import { handleDismissToast, handleUpdateToast } from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import styled from 'styled-components'

import {
  AiChatFeedbackThumbsDownButtonTappedType,
  AiChatFeedbackThumbsUpButtonTappedType,
  trackAiChatFeedbackThumbsDownButtonTappedPayload,
  trackAiChatFeedbackThumbsUpButtonTappedPayload,
} from 'generated/trackingEvents'
import {
  AI_ASSISTANT_COPY_ICON,
  AI_ASSISTANT_RATING_CONTAINER,
  AI_ASSISTANT_RATING_THUMBS_DOWN,
  AI_ASSISTANT_RATING_THUMBS_UP,
} from 'utils/testing/testIDs'

import { RatingDrawer } from './RatingDrawer'
import { AI_CHAT_FEEDBACK_ORIGIN } from './utils/constants'
import { RatingMutationPayload } from './utils/types'

const Container = styled.div`
  display: flex;
  position: relative;
  margin-left: 60px;
  width: 100px;
  height: 25px;
  margin-top: 10px;
  margin-bottom: 20px;
`

const IconContainer = styled.div`
  cursor: pointer;
  margin-right: 20px;
`

const ThumbsDownIcon = styled(AcornsIcon)`
  margin-top: -2px;
  & path {
    fill: ${theme.colors.primaryStone};
  }
`

const ThumbsUpIcon = styled(AcornsIcon)`
  & path {
    fill: ${theme.colors.primaryStone};
  }
`
const ThumbsDownFilledIcon = styled(AcornsIcon)`
  margin-top: -1px;
`

const ThumbsUpFilledIcon = styled(AcornsIcon)`
  margin-top: -2px;
`

const CopyIcon = styled(AcornsIcon)<{ hasCopied: boolean }>`
  & path {
    fill: ${(props) =>
      props.hasCopied ? theme.colors.primarySlate : theme.colors.primaryStone};
  }
`

const RATING_KEYS = {
  THUMBS_UP: 'THUMBS_UP',
  THUMBS_DOWN: 'THUMBS_DOWN',
}

const Rating = ({
  rating,
  messageId,
  onRateResponse,
  input,
}: {
  messageId?: string
  rating?: number
  onRateResponse: ({ messageId, rating }: RatingMutationPayload) => void
  input: string
}) => {
  const [hasCopied, setHasCopied] = useState(false)
  const [isFeedbackDrawerOpen, setIsFeedbackDrawerOpen] = useState(false)
  const [currentNotification, setCurrentNotification] = useState<
    string | number | undefined
  >(undefined)
  const { getRemoteString } = useRemoteStrings()
  const { track } = useAnalyticsClient()

  const handleCopyToClipboard = async () => {
    await navigator.clipboard.writeText(input)
    setHasCopied(true)
    if (currentNotification) {
      handleDismissToast(currentNotification)
    }

    const newToast = handleUpdateToast({
      body: getRemoteString({
        key: 'acorns_intelligence.chat.toast.copy.body',
      }),
    })
    setCurrentNotification(newToast as string)

    setTimeout(() => {
      setHasCopied(false)
    }, 5000)
  }

  const handleShowFeedbackDrawer = () => {
    setIsFeedbackDrawerOpen(true)
  }

  const handleThumbsUp = async () => {
    const isNewRating = rating !== 1
    onRateResponse({
      messageId: messageId || '',
      ...(isNewRating && { rating: 1 }),
    })
    if (isNewRating) {
      track(
        ...trackAiChatFeedbackThumbsUpButtonTappedPayload({
          origin: AI_CHAT_FEEDBACK_ORIGIN,
          contentId: messageId || '',
          type: AiChatFeedbackThumbsUpButtonTappedType.Bot_message,
        }),
      )
      handleDismissToast(currentNotification)
      const newNotification = handleUpdateToast({
        body: getRemoteString({
          key: 'acorns_intelligence.chat.toast.thumbs_up.body',
        }),
        showIcon: true,
      })
      setCurrentNotification(newNotification)
    }
  }

  const handleThumbsDown = () => {
    const isNewRating = rating !== 0
    onRateResponse({
      messageId: messageId || '',
      ...(isNewRating && { rating: 0 }),
    })

    if (isNewRating) {
      track(
        ...trackAiChatFeedbackThumbsDownButtonTappedPayload({
          origin: AI_CHAT_FEEDBACK_ORIGIN,
          contentId: messageId || '',
          type: AiChatFeedbackThumbsDownButtonTappedType.Bot_message,
        }),
      )
      handleDismissToast(currentNotification)
      const newNotification = handleUpdateToast({
        body: getRemoteString({
          key: 'acorns_intelligence.chat.toast.thumbs_down.body',
        }),
        linkText: getRemoteString({
          key: 'acorns_intelligence.chat.toast.thumbs_down.cta',
        }),
        showIcon: false,
        onClick: handleShowFeedbackDrawer,
      })
      setCurrentNotification(newNotification)
    }
  }

  const handleRateResponse = async (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (event.currentTarget.dataset.rating === RATING_KEYS.THUMBS_UP) {
      handleThumbsUp()
    } else {
      handleThumbsDown()
    }
  }

  return (
    <Container data-testid={AI_ASSISTANT_RATING_CONTAINER}>
      <IconContainer
        data-testid={AI_ASSISTANT_RATING_THUMBS_UP}
        data-rating={RATING_KEYS.THUMBS_UP}
        onClick={handleRateResponse}
      >
        {rating === 1 ? (
          <ThumbsUpFilledIcon
            icon={AcornsIcon.Icon.ThumbsUpFilled}
            width="24px"
            height="24px"
          />
        ) : (
          <ThumbsUpIcon
            icon={AcornsIcon.Icon.ThumbsUp}
            width="24px"
            height="24px"
          />
        )}
      </IconContainer>
      <IconContainer
        data-testid={AI_ASSISTANT_RATING_THUMBS_DOWN}
        data-rating={RATING_KEYS.THUMBS_DOWN}
        onClick={handleRateResponse}
      >
        {rating === 0 ? (
          <ThumbsDownFilledIcon
            icon={AcornsIcon.Icon.UtilityDownFilledFv}
            width="24px"
            height="24px"
          />
        ) : (
          <ThumbsDownIcon
            icon={AcornsIcon.Icon.ThumbsDown}
            width="24px"
            height="24px"
          />
        )}
      </IconContainer>
      <IconContainer
        data-testid={AI_ASSISTANT_COPY_ICON}
        onClick={hasCopied ? undefined : handleCopyToClipboard}
      >
        <CopyIcon
          hasCopied={hasCopied}
          icon={
            hasCopied
              ? AcornsIcon.Icon.UtilityLargeCheckmarkFv
              : AcornsIcon.Icon.UtilityCopyFv
          }
          width="24px"
          height="24px"
        />
      </IconContainer>
      <RatingDrawer
        isFeedbackDrawerOpen={isFeedbackDrawerOpen}
        setIsFeedbackDrawerOpen={setIsFeedbackDrawerOpen}
        messageId={messageId ?? ''}
      />
    </Container>
  )
}

export { Rating }
